import { StripeV2SubscriptionPrice } from '@/services/stripe/dto';

import type { ApplePayRecurringPaymentRequestIntervalUnit } from '@stripe/stripe-js/dist/stripe-js/elements/apple-pay';

export type WebSubscriptionPlanType =
  | 'pro.1w'
  | 'pro.1w_19_99'
  | 'pro.1m'
  | 'pro.1m_29_99'
  | 'pro.3m'
  | 'pro.1y'
  | 'pro.one_off_1d_9_99'
  | 'pro.one_off_1d_14_99'
  | 'pro.one_off_1d_19_99'
  // Rebill plans
  | 'pro.rebill_1w_discount'
  | 'pro.rebill_1w_discount_8_99'
  | 'pro.rebill_1w_discount_9_99'
  | 'pro.rebill_1w_discount_11_99'
  | 'pro.rebill_1m_discount'
  | 'pro.rebill_3m_discount'
  | 'pro.rebill_week_19_99'
  | 'pro.rebill_week_9_99_to_21_99'
  | 'pro.rebill_week_14_99_to_29_99'
  | 'pro.rebill_month_29_99'
  | 'pro.rebill_month_29_99_to_39_99'
  | 'pro.rebill_2weeks_39_99'
  | 'pro.rebill_month_49_99'
  | 'pro.rebill_3months_59_99'
  | 'pro.rebill_3months_59_99_to_79_99'
  | 'pro.rebill_year_79_99_to_159_99';

export type WebCoinsPlanType =
  | 'com.pheon.coins.plan6'
  | 'com.pheon.coins.plan9';

export type WebSubscriptionPlanConfigType = {
  display: string;
  displayRebill: string;
  basePrice: number;
  basePriceWithoutSale: number;
  monthMultiplier: number;
  daysPeriod: number;
  basePricePerDay: number;
  basePricePerDayWithoutSale: number;
  recurringPaymentIntervalCount: number;
  recurringPaymentIntervalUnit: ApplePayRecurringPaymentRequestIntervalUnit;
};

const SUBSCRIPTION_PLANS_CONFIG = {
  proWeekly(
    basePrice: number,
    basePriceWithoutSale: number = 40,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 7;

    return {
      display: '1-Week Plan',
      displayRebill: '7 days',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 0.25,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 7,
      recurringPaymentIntervalUnit: 'day',
    };
  },
  proWeekly899(
    basePrice: number,
    basePriceWithoutSale: number = 8.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 7;

    return {
      display: '1-Week Plan $8.99',
      displayRebill: '7 days',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 0.25,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 7,
      recurringPaymentIntervalUnit: 'day',
    };
  },
  proWeekly1999(
    basePrice: number,
    basePriceWithoutSale: number = 19.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 7;

    return {
      display: '1-Week Plan $19.99',
      displayRebill: '7 days',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 0.25,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 7,
      recurringPaymentIntervalUnit: 'day',
    };
  },
  proWeekly1199(
    basePrice: number,
    basePriceWithoutSale: number = 11.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 7;

    return {
      display: '1-Week Plan $11.99',
      displayRebill: '7 days',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 0.25,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 7,
      recurringPaymentIntervalUnit: 'day',
    };
  },
  proMonthly(
    basePrice: number,
    basePriceWithoutSale: number = 70,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 30;
    return {
      display: '1-Month Plan',
      displayRebill: '1 month',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 1,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 1,
      recurringPaymentIntervalUnit: 'month',
    };
  },
  proThreeMonths(
    basePrice: number,
    basePriceWithoutSale: number = 150,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 90;
    return {
      display: '3-Month Plan',
      displayRebill: '3 months',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 3,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 3,
      recurringPaymentIntervalUnit: 'month',
    };
  },
  proYearly(
    basePrice: number,
    basePriceWithoutSale: number = 275,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 365;
    return {
      display: '12-Month Plan',
      displayRebill: '1 year',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 12,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 1,
      recurringPaymentIntervalUnit: 'year',
    };
  },
  proOneOff1d999(
    basePrice: number,
    basePriceWithoutSale: number = 9.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 1;
    return {
      display: 'One Day VIP',
      displayRebill: '1 day',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 0.033,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 1,
      recurringPaymentIntervalUnit: 'day',
    };
  },
  proOneOff1d1499(
    basePrice: number,
    basePriceWithoutSale: number = 14.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 1;
    return {
      display: 'One Day VIP',
      displayRebill: '1 day',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 0.033,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 1,
      recurringPaymentIntervalUnit: 'day',
    };
  },
  proOneOff1d1999(
    basePrice: number,
    basePriceWithoutSale: number = 19.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 1;
    return {
      display: 'One Day VIP',
      displayRebill: '1 day',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 0.033,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 1,
      recurringPaymentIntervalUnit: 'day',
    };
  },
  proWeekly3999(
    basePrice: number,
    basePriceWithoutSale: number = 39.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 7;
    return {
      display: 'Rebill week 19.99',
      displayRebill: '7 days',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 0.25,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 7,
      recurringPaymentIntervalUnit: 'day',
    };
  },
  proMonthly2999(
    basePrice: number,
    basePriceWithoutSale: number = 29.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 30;
    return {
      display: '1-Month Plan $29.99',
      displayRebill: '1 month',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 1,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 1,
      recurringPaymentIntervalUnit: 'month',
    };
  },
  proMonthly5999(
    basePrice: number,
    basePriceWithoutSale: number = 59.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 30;
    return {
      display: 'Rebill month 29.99',
      displayRebill: '1 month',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 1,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 1,
      recurringPaymentIntervalUnit: 'month',
    };
  },
  proMonthly7999(
    basePrice: number,
    basePriceWithoutSale: number = 79.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 14;
    return {
      display: 'Rebill 2 week 39.99',
      displayRebill: '2 weeks',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 0.5,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 1,
      recurringPaymentIntervalUnit: 'month',
    };
  },
  proMonthly9999(
    basePrice: number,
    basePriceWithoutSale: number = 99.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 30;
    return {
      display: 'Rebill month 49.99',
      displayRebill: '1 month',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 1,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 1,
      recurringPaymentIntervalUnit: 'month',
    };
  },
  proThreeMonths11999(
    basePrice: number,
    basePriceWithoutSale: number = 119.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 90;
    return {
      display: 'Rebill 3 month 59.99',
      displayRebill: '3 months',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 3,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 3,
      recurringPaymentIntervalUnit: 'month',
    };
  },
  proWeekly999To2199(
    basePrice: number,
    basePriceWithoutSale: number = 21.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 7;
    return {
      display: '1-Week Plan $9.99',
      displayRebill: '7 days',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 0.25,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 7,
      recurringPaymentIntervalUnit: 'day',
    };
  },
  proWeekly1499To2999(
    basePrice: number,
    basePriceWithoutSale: number = 29.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 7;
    return {
      display: '1-Week Plan $14.99',
      displayRebill: '7 days',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 0.25,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 7,
      recurringPaymentIntervalUnit: 'day',
    };
  },
  proMonthly2999To3999(
    basePrice: number,
    basePriceWithoutSale: number = 39.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 30;
    return {
      display: 'Rebill month 29.99',
      displayRebill: '1 month',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 1,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 1,
      recurringPaymentIntervalUnit: 'month',
    };
  },
  proThreeMonths5999To7999(
    basePrice: number,
    basePriceWithoutSale: number = 79.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 90;
    return {
      display: 'Rebill 3 month 59.99',
      displayRebill: '3 months',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 3,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 3,
      recurringPaymentIntervalUnit: 'month',
    };
  },
  proYearly7999To15999(
    basePrice: number,
    basePriceWithoutSale: number = 159.99,
  ): WebSubscriptionPlanConfigType {
    const daysPeriod = 365;
    return {
      display: 'Rebill year 79.99',
      displayRebill: '1 year',
      basePrice,
      basePriceWithoutSale,
      monthMultiplier: 12,
      daysPeriod,
      basePricePerDay: Number((basePrice / daysPeriod).toFixed(2)),
      basePricePerDayWithoutSale: Number(
        (basePriceWithoutSale / daysPeriod).toFixed(2),
      ),
      recurringPaymentIntervalCount: 1,
      recurringPaymentIntervalUnit: 'year',
    };
  },
};
class WebSubscriptionsModule {
  getPlanConfig(
    subscription: StripeV2SubscriptionPrice,
  ): WebSubscriptionPlanConfigType {
    const plansMap: Record<
      WebSubscriptionPlanType,
      keyof typeof SUBSCRIPTION_PLANS_CONFIG
    > = {
      'pro.1w': 'proWeekly',
      'pro.1w_19_99': 'proWeekly',
      'pro.1m': 'proMonthly',
      'pro.1m_29_99': 'proMonthly',
      'pro.3m': 'proThreeMonths',
      'pro.1y': 'proYearly',
      'pro.one_off_1d_9_99': 'proOneOff1d999',
      'pro.one_off_1d_14_99': 'proOneOff1d1499',
      'pro.one_off_1d_19_99': 'proOneOff1d1999',
      // Rebill plans mapping
      'pro.rebill_1w_discount': 'proWeekly',
      'pro.rebill_1w_discount_8_99': 'proWeekly899',
      'pro.rebill_1w_discount_9_99': 'proWeekly1999',
      'pro.rebill_1w_discount_11_99': 'proWeekly1199',
      'pro.rebill_1m_discount': 'proMonthly',
      'pro.rebill_3m_discount': 'proThreeMonths',
      'pro.rebill_week_19_99': 'proWeekly1999',
      'pro.rebill_week_9_99_to_21_99': 'proWeekly999To2199',
      'pro.rebill_week_14_99_to_29_99': 'proWeekly1499To2999',
      'pro.rebill_month_29_99': 'proMonthly5999',
      'pro.rebill_month_29_99_to_39_99': 'proMonthly2999To3999',
      'pro.rebill_2weeks_39_99': 'proMonthly7999',
      'pro.rebill_month_49_99': 'proMonthly9999',
      'pro.rebill_3months_59_99': 'proThreeMonths11999',
      'pro.rebill_3months_59_99_to_79_99': 'proThreeMonths5999To7999',
      'pro.rebill_year_79_99_to_159_99': 'proYearly7999To15999',
    };
    return SUBSCRIPTION_PLANS_CONFIG[plansMap[subscription.id]](
      subscription.price,
      subscription.original_price || undefined,
    );
  }
}
export const webSubscriptionsModule = new WebSubscriptionsModule();
